import { ActionTree, Module, MutationTree, GetterTree } from "vuex";
import axios from "axios";
import AuthState from "@/store/types/AuthState";
import AuthHelper from "@/services/AuthHelper";

interface TokenCredentials {
  codeVerifier: string;
  authorizationCode: string;
}

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  created_at: number;
}

export const state: AuthState = {
  accessToken: undefined,
  tokenType: undefined,
  codeVerifier: undefined,
  authorizationCode: undefined,
};

export const getters: GetterTree<AuthState, unknown> = {
  accessToken(state): string | undefined {
    return state.accessToken;
  },

  codeVerifier(state): string | undefined {
    return state.codeVerifier;
  },

  authorizationCode(state): string | undefined {
    return state.authorizationCode;
  },
};

export const mutations: MutationTree<AuthState> = {
  setCodeVerifier(state, code: string) {
    state.codeVerifier = code;
  },

  setAuthorizationCode(state, code: string) {
    state.authorizationCode = code;
  },

  updateTokenData(state, payload: TokenResponse) {
    state.accessToken = payload.access_token;
    state.tokenType = payload.token_type;
  },

  updateRequestHeader(state) {
    if (state.accessToken && state.tokenType) {
      axios.defaults.headers.common[
        "Authorization"
      ] = `${state.tokenType} ${state.accessToken}`;
    } else {
      axios.defaults.headers.common["Authorization"] = null;
    }
  },

  clearAuthorizationData(state) {
    state.authorizationCode = undefined;
    state.codeVerifier = undefined;
  },

  clearSession(state) {
    state.accessToken = undefined;
    state.tokenType = undefined;
    state.codeVerifier = undefined;
  },
};

export const actions: ActionTree<AuthState, unknown> = {
  generateCodeVerifier({ commit }): string {
    const helper = new AuthHelper();
    const code = helper.generateRandomString();

    commit("setCodeVerifier", code);
    return code;
  },

  async requestToken({ state, commit }): Promise<string | undefined> {
    if (!(state.codeVerifier || state.authorizationCode)) {
      return Promise.reject(undefined);
    }

    const data = {
      client_id: AuthHelper.clientID,
      redirect_uri: AuthHelper.redirectUri,
      grant_type: "authorization_code",
      code: state.authorizationCode,
      code_verifier: state.codeVerifier,
    };

    try {
      const response = await axios({
        url: `${AuthHelper.endpoint}/token`,
        data: data,
        method: "POST",
      });
      commit("updateTokenData", response.data);
      commit("updateRequestHeader");
      commit("clearAuthorizationData");

      return Promise.resolve(state.accessToken);
    } catch (error) {
      return Promise.reject(undefined);
    }
  },

  async authenticate({ state }): Promise<string | undefined> {
    if (state.accessToken !== undefined) {
      return Promise.resolve(state.accessToken);
    }
  },
};

export const auth: Module<AuthState, unknown> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
